import { Interior } from '../Checklist/Interior';
import { DynamicOperations } from '../Checklist/DynamicOperations';
import { ElectricHybrid } from '../Checklist/ElectricHybrid';
import { EngineRunning } from '../Checklist/EngineRunning';
import { SelectOption } from '../General/Select';

export type VehicleInspectionPermissions = {
  canView: boolean;
  canEditInspection: boolean;
  canEditDamage: boolean;
  canEditFr: boolean;
  canEditParts: boolean;
  canApproveDamage: boolean;
  canChangePartComponent: boolean;
  canChangeDamageType: boolean;
  canChangeSeverity: boolean;
  canChangeRepairMethod: boolean;
  canChangeWorkCenter: boolean;
  canRemoveDamageLine: boolean;
  canChangeStatus: boolean;
  nextStatuses: SelectOption<number>[];
};

export type Inspection = {
  // inspection page cards:
  idx: number;
  creationDateUTC: string;
  id: number;
  customerCode: string;
  inspectionRef: string;
  vin: string;
  regNumber: string;
  manufacturer: string;
  model: string;
  colour: string;
  customerId: number;
  customerName: string;
  subCustomerId: number;
  subCustomerName: string;
  fleetCode: string;
  fleetDescription: string;
  inspectionDateTime: string;
  inspector: string;
  locationId: number;
  inspectionLocation: string;
  inspectionStatus: number;
  inspectionStatusName: InspectionStatusMap;
  inspectionType: string;
  inspectionTypeDesc: string;
  approvalDateTime: string;
  inspectionCost: number;
  cumulativeCost: number;
  cumulativeApprovalCost: number;
  cumulativeGradePoints: number;
  cumulativeGradeScore: number;
  finalCost: number;
  finalApprovalCost: number;
  finalGradePoints: number;
  finalGradeScore: number;
  /** tabs: */
  // inspection:
  mileage: number;
  odometerImageFileName: string;
  // wheels:
  frontLeftTyreBrand: string;
  frontLeftWheelType: string;
  frontLeftTyreWidth: number;
  frontLeftTyreHeight: number;
  frontLeftRimRadius: number;
  frontLeftTyreLoad: number;
  frontLeftTyreSpeed: string;
  frontLeftWheelDepth: string;
  frontLeftImageFileUrl: string;
  frontRightTyreBrand: string;
  frontRightWheelType: string;
  frontRightTyreWidth: number;
  frontRightTyreHeight: number;
  frontRightRimRadius: number;
  frontRightTyreLoad: number;
  frontRightTyreSpeed: string;
  frontRightWheelDepth: string;
  frontRightImageFileUrl: string;
  rearLeftTyreBrand: string;
  rearLeftWheelType: string;
  rearLeftTyreWidth: number;
  rearLeftTyreHeight: number;
  rearLeftRimRadius: number;
  rearLeftTyreLoad: number;
  rearLeftTyreSpeed: string;
  rearLeftWheelDepth: string;
  rearLeftImageFileUrl: string;
  rearRightTyreBrand: string;
  rearRightWheelType: string;
  rearRightTyreWidth: number;
  rearRightTyreHeight: number;
  rearRightRimRadius: number;
  rearRightTyreLoad: number;
  rearRightTyreSpeed: string;
  rearRightWheelDepth: string;
  rearRightImageFileUrl: string;
  spareTyreBrand: string;
  spareWheelType: string;
  spareTyreWidth: number;
  spareTyreHeight: number;
  spareRimRadius: number;
  spareTyreLoad: number;
  spareTyreSpeed: string;
  spareWheelDepth: string;
  spareImageFileUrl: string;
  // views:
  frontImageFileUrl: string;
  rearImageFileUrl: string;
  nearsideImageFileUrl: string;
  offsideImageFileUrl: string;
  chargeLevelImageFileUrl: string;
  chargeCablesImageFileUrl: string;
  keysImageFileUrl: string;
  istaCheckListImageUrl: string;
  odometerResizedImageUrl: string;
  frontLeftResizedImageUrl: string;
  frontRightResizedImageUrl: string;
  rearLeftResizedImageUrl: string;
  rearRightResizedImageUrl: string;
  spareResizedImageUrl: string;
  frontResizedImageUrl: string;
  rearResizedImageUrl: string;
  nearsideResizedImageUrl: string;
  offsideResizedImageUrl: string;
  chargeLevelResizedImageUrl: string;
  chargeCablesResizedImageFileUrl: string;
  keysImageResizedFileUrl: string;
  istaCheckListResizedImageUrl: string;
  tnrStatus: string;
  tnrAnnounced: string;
  jobCompleted: string;
  otherInspections: OtherInspection[];
  permissions?: VehicleInspectionPermissions;
};

export type OtherInspection = {
  id: number;
  inspectionDateTime: string;
  inspectionLocation: string;
  inspectionStatusName: string;
  authorisedApprovalCost: number;
};

export type InspectionFilters = {
  inspectionStatus: SelectOption<number>[];
  inspectionLocations: SelectOption<string>[];
  inspectionTypes: SelectOption<string>[];
  fleetCodes: SelectOption<string>[];
  inspectors: SelectOption<string>[];
  tnrStatuses: SelectOption<string>[];
  customers: SelectOption<number>[];
};

export type UpdateInspectionStatus = {
  id: number;
  inspectionStatus: number;
  forceChange: boolean;
  authorizationNotes?: string;
};

export type UpdateInspectionStatusResponse = {
  entities: Array<{
    message: string;
    result: string;
  }>;
};

export type DamagesImage = {
  id: number;
  imageFileUrl: string;
  resizedImageUrl: string;
};
export type DamagesImagesData = {
  entities: DamagesImage[];
};

export type Wheel = {
  tyreBrand: string;
  wheelType: string;
  tyreWidth: number;
  tyreHeight: number;
  rimRadius: number;
  tyreLoad: number;
  tyreSpeed: string;
  wheelInnerDepth: string;
  wheelMiddleDepth: string;
  wheelOuterDepth: string;
};

export type WheelDetails = {
  frontLeftWheel: Wheel;
  frontRightWheel: Wheel;
  rearLeftWheel: Wheel;
  rearRightWheel: Wheel;
  spareWheel: Wheel;
};

export type ReturnInspectionDamageDetails = Array<{
  areaCode: string;
  damageAreaDesc: string;
  partCode: string;
  damagePartDesc: string;
  responsibilityCode: string;
  responsibilityDesc: string;
  damageType: string;
  damageTypeDesc: string;
  dealerCode: string;
  sector: string;
  damageCauseCode: string;
  damageConditionCode: string;
  severityId: number;
  severityDesc: string;
  internalGradingId: number;
  workCenterCode: string;
  workCenterDesc: string;
  note: string;
  mechanicalFailure: null | string;
  imageFileNames: string[];
  imageFileUrls: string[];
}>;

export type ReportInitial = {
  regNumber: string;
  model: string;
  cumulativeGradeScore: string;
  finalGradePoints: number;
  finalGradeScore: number;
  colour: string;
  mileage: string;
  vin: string;
  inspectionDateTime: string;
  manufacturer: string;
  mechanicalInspectionInteriorCheckList: Interior;
  mechanicalInspectionElectricCheckList: ElectricHybrid;
  mechanicalInspectionIstaCheckList: DynamicOperations;
  mechanicalInspectionRunningEngineBayCheckList: EngineRunning;
  mechanicalInspectionWheelDetails: WheelDetails;
  returnInspectionDamageDetails: ReturnInspectionDamageDetails;
  returnInspectionOdometer: {
    mileage: number;
    odometerImageFileName: string;
    odometerImageFileUrl: string;
  };
};

export type ReportInitialData = {
  entities: ReportInitial[];
};

export type TNRDecision = {
  vin: string;
  override: string;
};

export type TNROverride = TNRDecision;

export type TNROverrideExample = {
  overrideId: number;
  vin: string;
  override: string;
  processed: string;
  processedDate: string;
  registrationDate: string;
  netPrice: number;
  damageCost: number;
};

export type TNRData = {
  entities: TNROverrideExample[];
};

export type TNRPreAppraisal = TNRDetails;

export type TNRPostAppraisal = TNRDetails;

export type TNRDetails = {
  vin: string;
  id: number;
};
export type MOTData = {
  registration: string;
  make: string;
  model: string;
  fuelType: string;
  primaryColour: string;
  registrationDate: string;
  manufactureDate: string;
  manufactureYear: string;
  motTestDueDate: string;
  hasOutstandingRecall: string;
};

export type ReplaceImageData = {
  id: number;
  imageType: number;
  imageFileName: string;
  imageFileBase64: string;
};

export type UpdateMileageData = {
  id: number;
  mileage: number;
};

export type UpdateRegData = {
  id: number;
  vin: string;
  newReg: string;
};

export type UpdateMechanicalInspection = {
  id: number;
  vin: string;
  mechanicalInspectionInformation: {
    interiorCheckList: Omit<Interior, 'id'>;
    electricCheckList: Omit<ElectricHybrid, 'id'>;
    runningEngineBayCheckList: Omit<EngineRunning, 'id'>;
    wheelDetails: WheelDetails;
    istaCheckList: Omit<DynamicOperations, 'id'>;
    summary: {
      comments: string;
    };
  };
};

export enum InspectionStatusMap {
  Inspected = 'Inspected',
  RequiredSubCustomerApproval = 'Required SubCustomer Approval',
  SubCustomerReview = 'SubCustomer Review',
  SubCustomerAudit = 'SubCustomer Audit',
  AwaitingAuthorisation = 'Awaiting Authorisation',
  RequiresPartsPricing = 'Requires Parts Pricing',
  RequiresReview = 'Requires Review',
  RequiresReSubmissionReview = 'Requires Re-submission Review',
  Authorised = 'Authorised',
  AuthorisedOrderParts = 'Authorised - Order Parts',
  AuthRejected = 'Auth Rejected',
  Completed = 'Completed',
}
